import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as FormikForm, Formik } from 'formik';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  LeaderboardConfigFragment,
  LeaderboardConfigInput,
  SegmentStatus,
  useCreateUpdateLeaderboardConfigMutation,
  useGetLeaderboardConfigsQuery,
  useGetUserSegmentsQuery,
} from 'api';
import { Capability, Loaders, PaddedPaper } from 'components';
import { LeaderboardSchema } from 'features/admin/validations';
import { useUpdateLeaderboard } from 'features/admin/updaters';
import { useNotify } from 'hooks';

import { Create, Leaderboard } from '../components/Leaderboards';

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  help: {
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(3),
  },
  customText: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
}));

const LeaderboardsIndex: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notify = useNotify();

  const mutationOptions = useUpdateLeaderboard();
  const { mutate, isLoading } = useCreateUpdateLeaderboardConfigMutation({
    onSuccess: editNotify,
    ...mutationOptions,
  });

  const leaderboardConfigsQueryResult = useGetLeaderboardConfigsQuery(null, {
    onError: notify.queryError,
  });

  const segmentsQueryResult = useGetUserSegmentsQuery(
    { segment_status: SegmentStatus.Active },
    {
      onError: notify.queryError,
    }
  );

  function getEditValues(values: LeaderboardConfigInput) {
    const { id, leaderboard_status, description, segment_ids, start_at, end_at, title } = values;
    return { id, leaderboard_status, description, segment_ids, start_at, end_at, title };
  }

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  function handleEdit(config: LeaderboardConfigFragment, values: LeaderboardConfigInput) {
    mutate({ data: values });
  }

  function renderConfig(config: LeaderboardConfigFragment) {
    return (
      <FormikForm>
        <Leaderboard
          segments={segmentsQueryResult.data.getUserSegments}
          config={config}
          working={isLoading}
        />
      </FormikForm>
    );
  }

  function renderForm() {
    const { getLeaderboardConfigs } = leaderboardConfigsQueryResult.data;

    return (
      <Grid container direction="column" spacing={3} wrap="nowrap">
        {getLeaderboardConfigs.map(config => (
          <Grid key={config.id} item>
            <Formik
              initialValues={getEditValues(config)}
              onSubmit={values => handleEdit(config, values)}
              validationSchema={LeaderboardSchema(config.all_users)}
              render={() => renderConfig(config)}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (leaderboardConfigsQueryResult.isLoading || segmentsQueryResult.isLoading) {
    return <Loaders.Standard />;
  }

  return (
    <Fragment>
      <PaddedPaper>
        <Typography variant="subtitle2">{t('admin:userLeaderboards')}</Typography>
        <Divider className={classes.divider} />
        <Typography className={classes.help} variant="subtitle2" paragraph>
          {t('admin:leaderboardsHelp')}
        </Typography>
        <Capability name="Admin.Leaderboards.Function.CreateOtherLeaderboards">
          <Create />
        </Capability>
      </PaddedPaper>
      <Typography variant="subtitle2" className={classes.customText}>
        {t('common:leaderboard_plural')}
      </Typography>
      {renderForm()}
    </Fragment>
  );
};

export default LeaderboardsIndex;
